<template>
  <v-dialog v-model="visible" max-width="920" scrollable>
    <v-card>
      <v-card-title class="text-subtitle-1 pb-1">
        <v-icon class="mr-2" color="green darken-1">mdi-clipboard-check-outline</v-icon>
        Piano vs posizionamento
        <span v-if="stagione" class="text-body-2 grey--text ml-2">— {{ stagione.anno }}</span>
        <v-spacer/>
        <v-btn icon small @click="visible = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-divider/>

      <v-card-text class="pa-0">
        <div class="d-flex align-center px-4 pt-3 pb-1" style="gap:16px">
          <v-switch
            v-model="nascondi_non_iniziati"
            dense hide-details
            label="Nascondi non iniziati"
            class="mt-0 pt-0"
          />
          <v-spacer/>
          <span class="caption grey--text">{{ righe_filtrate.length }} prodotti</span>
        </div>

        <v-simple-table dense>
          <thead>
            <tr>
              <th colspan="2" class="caption grey--text">Prodotto</th>
              <th class="caption grey--text text-right">Pianificato</th>
              <th class="caption grey--text text-right">Posizionato</th>
              <th class="caption grey--text text-right" style="min-width:70px">%</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="gruppo in righe_per_specie">
              <!-- Intestazione specie -->
              <tr :key="'g-' + gruppo.nome" class="specie-header">
                <td colspan="5" class="caption font-weight-bold grey--text text--darken-1 py-1 pl-3">
                  {{ gruppo.nome }}
                </td>
              </tr>
              <template v-for="sub in gruppo.diametri">
                <!-- Intestazione diametro -->
                <tr :key="'g-' + gruppo.nome + '-d-' + sub.diam" class="diam-header">
                  <td colspan="5" class="caption grey--text text--darken-1 py-1 pl-6">
                    Ø {{ sub.diam }}
                    <span class="font-weight-regular ml-1">({{ sub.righe.length }})</span>
                  </td>
                </tr>
                <!-- Righe prodotti -->
                <tr v-for="item in sub.righe" :key="item.id">
                  <td style="width:38px; padding-right:0; padding-left:24px">
                    <template v-if="!item.component_infos || item.component_infos.length <= 1">
                      <img v-if="item.img_url" :src="item.img_url" class="avatar-swatch"/>
                      <div v-else class="avatar-swatch" :style="{ backgroundColor: item.color }"/>
                    </template>
                    <svg v-else class="avatar-swatch" viewBox="0 0 28 28" width="28" height="28">
                      <defs>
                        <clipPath
                          v-for="(inf, idx) in item.component_infos"
                          :key="`cpcf-${item.id}-${idx}`"
                          :id="`pcf-${item.id}-${idx}`"
                        >
                          <path :d="_sectorPath(idx, item.component_infos.length, 14, 14, 14)"/>
                        </clipPath>
                      </defs>
                      <g v-for="(inf, idx) in item.component_infos" :key="`seccf-${item.id}-${idx}`">
                        <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="28" height="28"
                               :clip-path="`url(#pcf-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                        <path v-else :d="_sectorPath(idx, item.component_infos.length, 14, 14, 14)"
                              :fill="inf.fallback"/>
                      </g>
                      <circle cx="14" cy="14" r="13.5" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
                    </svg>
                  </td>
                  <td style="line-height:1.3; padding-top:6px; padding-bottom:6px">
                    <div style="line-height:1">
                      <div v-if="item.tipo_contenitore" class="text-caption grey--text text--darken-1" style="line-height:1;margin-bottom:1px">{{ item.tipo_contenitore }}</div>
                      <div v-if="item.specie_nomi" class="text-caption grey--text font-italic" style="line-height:1">{{ item.specie_nomi }}</div>
                    </div>
                    <div v-for="(riga, ri) in item.righe_dettaglio" :key="ri" class="text-body-2 d-flex align-center" style="gap:5px">
                      <span
                        :title="riga.hex || 'Nessun HEX'"
                        :style="{
                          display: 'inline-block',
                          width: '12px', height: '12px',
                          borderRadius: '3px',
                          border: '1px solid rgba(0,0,0,0.18)',
                          flexShrink: 0,
                          background: riga.hex
                            ? riga.hex
                            : 'linear-gradient(to bottom right, #fff 0%, #fff calc(50% - 0.8px), #e53935 calc(50% - 0.8px), #e53935 calc(50% + 0.8px), #fff calc(50% + 0.8px), #fff 100%)',
                        }"
                      />
                      {{ riga.testo }}
                    </div>
                    <div v-if="!item.righe_dettaglio || !item.righe_dettaglio.length" class="text-body-2">{{ item.label }}</div>
                  </td>
                  <td class="text-body-2 text-right">{{ item.pianificato || '—' }}</td>
                  <td class="text-body-2 text-right" :class="{ 'green--text text--darken-2': item.posizionato > 0 }">
                    {{ item.posizionato || '—' }}
                  </td>
                  <td class="text-right">
                    <v-chip
                      v-if="item.posizionato > 0 && item.pct !== null"
                      x-small
                      :color="item.pct >= 100 ? 'green darken-1' : item.pct >= 50 ? 'orange darken-1' : 'grey'"
                      dark
                    >{{ item.pct }}%</v-chip>
                    <span v-else class="caption grey--text">—</span>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
          <tfoot v-if="righe.length">
            <tr class="grey lighten-4">
              <td colspan="2" class="caption font-weight-medium py-2">Totale</td>
              <td class="caption font-weight-medium text-right">{{ totale_pianificato }}</td>
              <td class="caption font-weight-medium text-right">{{ totale_posizionato }}</td>
              <td class="text-right">
                <v-chip
                  v-if="totale_pianificato > 0"
                  x-small
                  :color="totale_pct >= 100 ? 'green darken-1' : totale_pct >= 50 ? 'orange darken-1' : 'grey'"
                  dark
                >{{ totale_pct }}%</v-chip>
              </td>
            </tr>
          </tfoot>
        </v-simple-table>

        <div v-if="!righe.length" class="text-center pa-10 grey--text caption">
          Nessun prodotto pianificato per questa stagione.
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ConfruntoPianoPosizionamentoDialog",
  props: {
    righe:    { type: Array,  default: () => [] },
    stagione: { type: Object, default: null },
  },
  data() {
    return {
      visible:               false,
      nascondi_non_iniziati: false,
    };
  },
  computed: {
    righe_filtrate() {
      if (!this.nascondi_non_iniziati) return this.righe;
      return this.righe.filter((r) => r.posizionato > 0);
    },
    righe_per_specie() {
      const groups = {};
      const order = [];
      for (const r of this.righe_filtrate) {
        const sp = r.nome_specie || 'Sconosciuta';
        if (!groups[sp]) { groups[sp] = {}; order.push(sp); }
        const dk = r.diametro != null ? String(r.diametro) : '—';
        if (!groups[sp][dk]) groups[sp][dk] = [];
        groups[sp][dk].push(r);
      }
      return order.map((nome) => ({
        nome,
        diametri: Object.entries(groups[nome])
          .sort(([a], [b]) => (parseInt(a) || 0) - (parseInt(b) || 0))
          .map(([diam, righe]) => ({ diam: diam === '—' ? '—' : `${diam} cm`, righe })),
      }));
    },
    totale_pianificato() {
      return this.righe.reduce((a, r) => a + (r.pianificato || 0), 0);
    },
    totale_posizionato() {
      return this.righe.reduce((a, r) => a + (r.posizionato || 0), 0);
    },
    totale_pct() {
      return this.totale_pianificato > 0
        ? Math.round((this.totale_posizionato / this.totale_pianificato) * 100)
        : 0;
    },
  },
  methods: {
    open() { this.visible = true; },
    _sectorPath(i, n, r, cx, cy) {
      const a0 = (i / n) * 2 * Math.PI;
      const a1 = ((i + 1) / n) * 2 * Math.PI;
      const x0 = cx + r * Math.sin(a0);
      const y0 = cy - r * Math.cos(a0);
      const x1 = cx + r * Math.sin(a1);
      const y1 = cy - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      return `M ${cx},${cy} L ${x0.toFixed(3)},${y0.toFixed(3)} A ${r},${r} 0 ${large},1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
    },
  },
};
</script>

<style scoped>
.avatar-swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  object-fit: cover;
  display: block;
}
.specie-header td {
  background-color: #F5F5F5;
  border-top: 1px solid #E0E0E0;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
.diam-header td {
  background-color: #FAFAFA;
  border-top: 1px solid #EEEEEE;
}
</style>
